import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';

import { useChat } from '@/hooks/useChat';
import { useChatQuery, useSendMessageMutation } from '@/services/endpoints/chat';
import { useWsTokenQuery } from '@/services/endpoints/misc';
import { useAppSelector } from '@/store/hooks';
import { Loader } from '@/components/common/Loader';
import type { Chat, MessageData } from '@/types/chat';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/lib/format';

interface DisplayMessage {
  id: string;
  userId: number;
  message: string;
  createdAt: string;
  nickname?: string;
}

function fromChat(item: Chat): DisplayMessage {
  return {
    id: item.id,
    userId: item.user_id,
    message: item.message,
    createdAt: item.created_at,
  };
}

function fromLive(data: MessageData): DisplayMessage {
  return {
    id: data.id,
    userId: data.user_id,
    message: data.message,
    createdAt: data.created_at,
    nickname: data.nickname,
  };
}

export function ChatRoom() {
  const { id = '' } = useParams<{ id: string }>();
  const room = decodeURIComponent(id);
  const { t } = useTranslation('general');
  const me = useAppSelector((s) => s.auth.user);

  const wsTokenQuery = useWsTokenQuery(String(me?.id ?? ''), {
    skip: !me?.id,
  });

  const historyQuery = useChatQuery({ id: room, limit: 50 }, { skip: !room });

  const [draft, setDraft] = useState('');
  const [sendMessage, sendMessageState] = useSendMessageMutation();
  const [liveMessages, setLiveMessages] = useState<DisplayMessage[]>([]);

  useChat({
    room,
    token: wsTokenQuery.data?.token ?? null,
    onPublish: (raw) => {
      if (!raw || typeof raw !== 'object') return;
      const data = raw as MessageData;
      if (!data.id || !data.message) return;
      setLiveMessages((prev) =>
        prev.some((m) => m.id === data.id) ? prev : [...prev, fromLive(data)],
      );
    },
  });

  const messages = useMemo<DisplayMessage[]>(() => {
    const history = (historyQuery.data?.data ?? []).map(fromChat).reverse();
    const seen = new Set(history.map((m) => m.id));
    const live = liveMessages.filter((m) => !seen.has(m.id));
    return [...history, ...live];
  }, [historyQuery.data, liveMessages]);

  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sendMessageState.isLoading) return;
    setDraft('');
    try {
      await sendMessage({ id: room, message: text }).unwrap();
    } catch {
      setDraft(text);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3">
        {historyQuery.isLoading && (
          <div className="flex h-32 items-center justify-center">
            <Loader />
          </div>
        )}

        {!historyQuery.isLoading && messages.length === 0 && (
          <p className="py-10 text-center text-sm text-fg-muted">
            {t('chat_no_messages', { defaultValue: 'No messages yet — say hi!' })}
          </p>
        )}

        <ul className="flex flex-col gap-2">
          {messages.map((message) => {
            const mine = me?.id === message.userId;
            return (
              <li
                key={message.id}
                className={cn(
                  'flex max-w-[80%] flex-col gap-0.5 rounded-2xl px-3 py-2 text-sm',
                  mine
                    ? 'self-end bg-brand text-brand-fg'
                    : 'self-start bg-bg-secondary text-fg',
                )}
              >
                {!mine && message.nickname && (
                  <span className="text-[11px] font-semibold text-chat-name">
                    {message.nickname}
                  </span>
                )}
                <span className="whitespace-pre-wrap break-words">{message.message}</span>
                <span
                  className={cn(
                    'text-[10px]',
                    mine ? 'text-brand-fg/70' : 'text-fg-muted',
                  )}
                >
                  {formatRelative(message.createdAt)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSend();
        }}
        className="flex items-center gap-2 border-t border-border bg-bg p-3"
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('chat_placeholder', { defaultValue: 'Write a message…' })}
          className="flex-1 rounded-full bg-bg-secondary px-4 py-2.5 text-sm text-fg placeholder:text-fg-muted outline-none focus:ring-2 focus:ring-brand/50"
        />
        <button
          type="submit"
          disabled={!draft.trim() || sendMessageState.isLoading}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-brand-fg disabled:opacity-50"
          aria-label="Send"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
